import {
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { TQualificationStatusOption } from 'modules/products/pages/interfaces';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, BoxSection } from '../../../app/components';
import { TableArea, TableWrapper } from './styles';
import { Table } from 'app/components';
import { useQueryParam, NumberParam, withDefault } from 'use-query-params';
import { TProducts, useQualificationsContext } from 'modules/products/context';
import {
  useProductsService,
  useQualificationsService,
} from 'modules/products/services/hooks';
import { EmptyContent } from 'modules/products/pages/ProductDetail/components/EmptyContent';
import {
  ConditionsRequiredProperty,
  TQualificationWithStatusAndProduct,
} from 'modules/customer/interfaces/qualifications';
import { PendenciesModal } from 'modules/customer/components/PendenciesModal';
import {
  useUploadBusinessFiles,
  useUploadPersonFiles,
} from 'modules/customer/hooks';
import get from 'lodash.get';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import { EBeneficiaryType } from 'modules/products/context/ProductsProvider/products.interfaces';
import {
  renderQualificationTableContent,
  tableQualificationHeader,
} from 'modules/common/QualificationTab/Qualification/Qualification';
import QualificationsSideSheet from './QualificationSideModal/QualificationsSideSheet';

interface IQualificationsTabProps {
  register: TRegisterBusinessDataRequest | TRegisterPersonDataRequest;
}
function isPersonRegister(
  register: TRegisterBusinessDataRequest | TRegisterPersonDataRequest,
): register is TRegisterPersonDataRequest {
  return !('legal_name' in register);
}

const QualificationsTab = ({ register }: IQualificationsTabProps) => {
  const isPerson = isPersonRegister(register);

  /* Hooks */
  const params = useParams();
  const {
    state: { borrowerStatusOptions },
  } = useQualificationsContext();
  const { getQualificationStatusOptions, getProductQualificationsLoading } =
    useQualificationsService();
  const { getProduct, getCustomVariables } = useProductsService();
  const { getQualifications: getQualificationsBusiness } = useCustomerService();
  const { getQualifications: getQualificationsPerson } = usePersonService();
  const { uploadedFiles: businessUploadedFilesState, fetchBusinessFile } =
    useUploadBusinessFiles();
  const { uploadedFiles: personUploadedFilesState, fetchPersonFile } =
    useUploadPersonFiles();
  const [linesPerPage, setLinesPerPage] = useQueryParam(
    'size',
    withDefault(NumberParam, 10),
  );
  const [currentPage, setCurrentPage] = useQueryParam(
    'page',
    withDefault(NumberParam, 0),
  );

  /* States */
  const [qualifications, setQualifications] = useState<
    TQualificationWithStatusAndProduct[]
  >([]);
  const [totalItemsQty, setTotalItemsQty] = useState(10);
  const [pendenciesModalOpen, updatePendenciesModalOpen] = useState<
    TQualificationWithStatusAndProduct | undefined
  >(undefined);
  const [finishRequest, setFinishRequest] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState<
    TQualificationWithStatusAndProduct | undefined
  >();

  /* Variables */
  const pid = params?.id as string;
  const isLoading = getProductQualificationsLoading;

  /* Handlers */
  const handleGetBorrowerQualification = async (
    personId: string,
    page = 0,
    itemsPerPage?: number,
  ) => {
    if (isPerson) {
      /* Fluxo PERSON: usePersonService.getQualifications(personId, page, itemsPerPage) */
      const userUploadedFiles =
        personUploadedFilesState.length === 0
          ? await fetchPersonFile(personId)
          : personUploadedFilesState;

      const borrowerContent = await getQualificationsPerson(
        personId,
        page,
        itemsPerPage,
      );
      if (borrowerContent) {
        setTotalItemsQty(borrowerContent?.total_elements || 0);
        const qualificationsList = await Promise.all(
          borrowerContent.content.map(async (qualification) => {
            try {
              const statusList: TQualificationStatusOption[] =
                await getQualificationStatusOptions(
                  qualification.product.id,
                  'PERSON',
                );
              const productData = await getProduct(qualification.product.id);
              const customVariables = await getCustomVariables(
                qualification.product.id,
              );
              const missingDocuments =
                (
                  productData?.data as TProducts
                ).variables_configuration?.borrower.person_document?.filter(
                  (document) => {
                    if (document.required) {
                      const inx = userUploadedFiles.findIndex(
                        (userUploadedFile) =>
                          document.type === userUploadedFile.type,
                      );
                      return inx === -1;
                    }
                    return false;
                  },
                ) || [];

              const missingVariables =
                customVariables?.borrower.person?.filter((variable) => {
                  if (variable.required) {
                    const variableValue = get(register, variable.name);
                    return typeof variableValue === 'undefined';
                  }
                  return false;
                }) || [];

              const missingConditions = (productData?.data as TProducts)
                .conditions_required
                ? ConditionsRequiredProperty.filter((condition) => {
                    const conditionValue = get(
                      qualification.condition,
                      condition.name,
                      null,
                    );
                    return conditionValue === null;
                  })
                : [];

              const missingBankOrPix =
                (!register.external_bank_account ||
                  !register.external_bank_account?.bank_account) &&
                (!register.pix || !register.pix?.key) &&
                (productData?.data as TProducts)?.beneficiary_type ===
                  EBeneficiaryType.BORROWER;

              return {
                ...qualification,
                statusList,
                missingDocuments,
                missingConditions,
                missingVariables,
                missingBankOrPix,
                productData: productData?.data as TProducts,
              };
            } catch {
              return null;
            }
          }),
        );
        setQualifications(
          qualificationsList.filter(
            (item) => item !== null,
          ) as TQualificationWithStatusAndProduct[],
        );
      } else {
        setTotalItemsQty(0);
        setQualifications([]);
      }
      setFinishRequest(true);
      return;
    }

    /* Fluxo BUSINESS: useCustomerService.getQualifications com BORROWER e EMPLOYER */
    const businessUploadedFiles =
      businessUploadedFilesState.length === 0
        ? await fetchBusinessFile(personId)
        : businessUploadedFilesState;

    const borrowerContent = await getQualificationsBusiness(
      personId,
      'BORROWER',
      page,
      itemsPerPage,
    );
    const employerContent = await getQualificationsBusiness(
      personId,
      'EMPLOYER',
      page,
      itemsPerPage,
    );
    if (borrowerContent) {
      setTotalItemsQty(
        (borrowerContent?.total_elements || 0) +
          (employerContent?.total_elements || 0),
      );

      const qualificationsList = await Promise.all(
        ([] as Promise<TQualificationWithStatusAndProduct | null>[]).concat(
          borrowerContent.content.map(async (qualification) => {
            try {
              const statusList: TQualificationStatusOption[] =
                await getQualificationStatusOptions(
                  qualification.product.id,
                  'BUSINESS',
                );
              const productData = await getProduct(qualification.product.id);
              const customVariables = await getCustomVariables(
                qualification.product.id,
              );
              const missingDocuments =
                (
                  productData?.data as TProducts
                ).variables_configuration?.borrower.business_document.filter(
                  (document) => {
                    if (document.required) {
                      const inx = businessUploadedFiles.findIndex(
                        (businessUploadedFile) =>
                          document.type === businessUploadedFile.type,
                      );
                      return inx === -1;
                    }
                    return false;
                  },
                ) || [];

              const missingVariables =
                customVariables?.borrower.business.filter((variable) => {
                  if (variable.required) {
                    const variableValue = get(register, variable.name);
                    return typeof variableValue === 'undefined';
                  }
                  return false;
                }) || [];

              const missingConditions = (productData?.data as TProducts)
                .conditions_required
                ? ConditionsRequiredProperty.filter((condition) => {
                    const conditionValue = get(
                      qualification.condition,
                      condition.name,
                      null,
                    );
                    return conditionValue === null;
                  })
                : [];

              const missingBankOrPix =
                (!register.external_bank_account ||
                  !register.external_bank_account?.bank_account) &&
                (!register.pix || !register.pix?.key) &&
                (productData?.data as TProducts)?.beneficiary_type ===
                  EBeneficiaryType.BORROWER;

              return {
                ...qualification,
                statusList,
                missingDocuments,
                missingConditions,
                missingVariables,
                missingBankOrPix,
                productData: productData?.data as TProducts,
              };
            } catch {
              return null;
            }
          }),
          employerContent
            ? employerContent.content.map(async (qualification) => {
                try {
                  const statusList: TQualificationStatusOption[] =
                    await getQualificationStatusOptions(
                      qualification.product.id,
                      'BUSINESS',
                    );
                  const productData = await getProduct(
                    qualification.product.id,
                  );
                  const customVariables = await getCustomVariables(
                    qualification.product.id,
                  );

                  const missingDocuments =
                    (
                      productData?.data as TProducts
                    ).variables_configuration?.employer_document.filter(
                      (document) => {
                        if (document.required) {
                          const inx = businessUploadedFiles.findIndex(
                            (businessUploadedFile) =>
                              document.type === businessUploadedFile.type,
                          );
                          return inx === -1;
                        }
                        return false;
                      },
                    ) || [];

                  const missingVariables =
                    customVariables?.employer.filter((variable) => {
                      if (variable.required) {
                        const variableValue = get(register, variable.name);
                        return typeof variableValue === 'undefined';
                      }
                      return false;
                    }) || [];

                  const missingBankOrPix =
                    (!register.external_bank_account ||
                      !register.external_bank_account?.bank_account) &&
                    (!register.pix || !register.pix?.key);

                  return {
                    ...qualification,
                    statusList,
                    missingDocuments,
                    missingConditions: [],
                    missingVariables,
                    missingBankOrPix,
                    productData: productData?.data as TProducts,
                  };
                } catch {
                  return null;
                }
              })
            : [],
        ),
      );
      setQualifications(
        qualificationsList.filter(
          (item) => item !== null,
        ) as TQualificationWithStatusAndProduct[],
      );
    } else {
      setTotalItemsQty(0);
      setQualifications([]);
    }
    setFinishRequest(true);
  };

  useEffect(() => {
    setFinishRequest(false);
    handleGetBorrowerQualification(pid, currentPage, linesPerPage);
  }, [currentPage, linesPerPage]);

  return (
    <BoxSection level="wrapper">
      {isLoading || !finishRequest ? (
        <div
          style={{
            paddingTop: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'center',
            width: '100%',
          }}
        >
          <Spinner />
        </div>
      ) : (!getProductQualificationsLoading && !borrowerStatusOptions) ||
        (qualifications?.length <= 0 && finishRequest) ? (
        <EmptyContent titleText="Nenhuma linha de crédito encontrada" />
      ) : (
        borrowerStatusOptions &&
        qualifications.length > 0 && (
          <TableArea style={{ marginTop: '40px' }}>
            <TableWrapper>
              <Table
                showPagination
                overflowVisible
                handlePageChange={(selectedPage) => {
                  setCurrentPage(selectedPage - 1);
                }}
                handleLinesPerPage={(selectedLinesPerPage) => {
                  setCurrentPage(0);
                  setLinesPerPage(
                    selectedLinesPerPage === 0 ? 10 : selectedLinesPerPage,
                  );
                }}
                totalElementsQty={totalItemsQty}
                currentPage={currentPage + 1}
                linesPerPage={linesPerPage}
                tableHeadCell={tableQualificationHeader}
                tableBodyContent={qualifications?.map((qualification) => {
                  return {
                    id: qualification?.id,
                    cells: renderQualificationTableContent(
                      qualification as TQualificationWithStatusAndProduct,
                      setSelectedQualification,
                    ),
                  };
                })}
              />
            </TableWrapper>
          </TableArea>
        )
      )}
      <PendenciesModal
        isOpen={pendenciesModalOpen !== undefined}
        qualification={pendenciesModalOpen}
        handleClose={() => updatePendenciesModalOpen(undefined)}
      />
      <QualificationsSideSheet
        isOpen={!!selectedQualification}
        handleClose={() => setSelectedQualification(undefined)}
        qualification={selectedQualification}
        productInfo={selectedQualification?.productData}
        borrowerQualificationData={selectedQualification}
      />
    </BoxSection>
  );
};

export default QualificationsTab;
